/**
 * Benefit Driver Section - Business Value Calculator
 * Sprint 7: Benefits Tab Redesign
 *
 * Renders one Level 1 benefit driver with:
 * - Enable/disable checkbox
 * - Parameter input grid
 * - Calculate button
 * - Results display (Year 1/2/3)
 * - Journey recommendation badge
 */

import React, { useState, useEffect } from 'react';
import {
  BenefitCalculator,
  CalculatorParameters,
  CalculationResult
} from './benefitCalculators';

interface DriverState {
  enabled: boolean;
  parameters: CalculatorParameters;
  results: CalculationResult | null;
  confidence: 'high' | 'medium' | 'low';
  attribution: number; // 0-100
}

interface BenefitDriverSectionProps {
  calculator: BenefitCalculator;
  state: DriverState;
  recommendationCount?: number;
  onChange: (updates: Partial<DriverState>) => void;
}

export const BenefitDriverSection: React.FC<BenefitDriverSectionProps> = ({
  calculator,
  state,
  recommendationCount = 0,
  onChange
}) => {
  const [localParams, setLocalParams] = useState<CalculatorParameters>(
    state.parameters || calculator.defaultParameters
  );

  // Sync local params with external state on mount
  useEffect(() => {
    if (state.parameters) {
      setLocalParams(state.parameters);
    }
  }, [state.parameters]);

  // Handle parameter input change
  const handleParamChange = (paramName: string, value: number) => {
    const updated = { ...localParams, [paramName]: value };
    setLocalParams(updated);
    onChange({ parameters: updated });
  };

  // Handle calculate button click
  const handleCalculate = () => {
    const result = calculator.calculate(localParams);
    onChange({ results: result, parameters: localParams });
  };

  // Handle checkbox toggle
  const handleToggle = (checked: boolean) => {
    onChange({ enabled: checked });
  };

  // Format currency for display
  const formatCurrency = (value: number): string => {
    if (value === 0) return '$0';
    if (value >= 1000000) {
      return `$${(value / 1000000).toFixed(1)}M`;
    } else if (value >= 1000) {
      return `$${(value / 1000).toFixed(0)}K`;
    }
    return `$${value.toLocaleString()}`;
  };

  const hasRecommendations = recommendationCount > 0;
  const hasResults = state.results !== null;

  return (
    <div
      className={`benefit-driver-section ${hasRecommendations ? 'has-recommendations' : ''} ${
        state.enabled ? 'enabled' : 'disabled'
      }`}
    >
      {/* Header with checkbox and title */}
      <div className="driver-header">
        <label className="driver-checkbox-label">
          <input
            type="checkbox"
            className="driver-checkbox"
            checked={state.enabled}
            onChange={(e) => handleToggle(e.target.checked)}
          />
          <span className="driver-title">
            {calculator.driverLabel}
            {hasRecommendations && (
              <span className="recommendation-badge" title="Recommendations from journey">
                {recommendationCount} rec{recommendationCount !== 1 ? 's' : ''}
              </span>
            )}
          </span>
        </label>
        {hasResults && state.results && (
          <div className="driver-value-display">
            {formatCurrency(state.results.year1)}
          </div>
        )}
      </div>

      {/* Collapsible content (only show if enabled) */}
      {state.enabled && (
        <div className="driver-content">
          {/* Description */}
          <p className="driver-description">{calculator.description}</p>

          {/* Formula display */}
          <div className="driver-formula">
            <strong>Formula:</strong> <code>{calculator.formula}</code>
          </div>

          {/* Parameters grid */}
          <div className="parameters-grid">
            {calculator.parameters.map((param) => (
              <div key={param.name} className="parameter-field">
                <label htmlFor={`param-${calculator.driverId}-${param.name}`} className="parameter-label">
                  {param.label}
                  {param.tooltip && (
                    <span className="parameter-tooltip" title={param.tooltip}>
                      ⓘ
                    </span>
                  )}
                </label>
                <div className="parameter-input-wrapper">
                  <input
                    type="number"
                    id={`param-${calculator.driverId}-${param.name}`}
                    className="parameter-input"
                    value={localParams[param.name] ?? param.min ?? 0}
                    min={param.min}
                    max={param.max}
                    step={param.step ?? 1}
                    onChange={(e) =>
                      handleParamChange(param.name, parseFloat(e.target.value) || 0)
                    }
                  />
                  {param.unit && <span className="parameter-unit">{param.unit}</span>}
                </div>
              </div>
            ))}
          </div>

          {/* Calculate button */}
          <div className="driver-actions">
            <button
              type="button"
              className="calculate-button"
              onClick={handleCalculate}
            >
              Calculate Benefits
            </button>
          </div>

          {/* Results display */}
          {hasResults && state.results && (
            <div className="results-display">
              <div className="results-header">Calculated Benefits</div>
              <div className="results-grid">
                <div className="result-item">
                  <div className="result-label">Year 1</div>
                  <div className="result-value">{formatCurrency(state.results.year1)}</div>
                </div>
                <div className="result-item">
                  <div className="result-label">Year 2</div>
                  <div className="result-value">{formatCurrency(state.results.year2)}</div>
                </div>
                <div className="result-item">
                  <div className="result-label">Year 3</div>
                  <div className="result-value">{formatCurrency(state.results.year3)}</div>
                </div>
                <div className="result-item total">
                  <div className="result-label">3-Year Total</div>
                  <div className="result-value">{formatCurrency(state.results.total)}</div>
                </div>
              </div>

              {/* Details */}
              {state.results.details && Object.keys(state.results.details).length > 0 && (
                <div className="results-details">
                  <div className="details-header">Calculation Details</div>
                  <ul className="details-list">
                    {Object.entries(state.results.details).map(([key, value]) => (
                      <li key={key}>
                        <strong>{key.replace(/_/g, ' ')}:</strong>{' '}
                        {typeof value === 'number'
                          ? value.toLocaleString()
                          : value}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
